import {
  faAngleUp,
  faAngleDown,
  faGripDotsVertical,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useRef, useEffect, useCallback } from "react";
import Button from "../buttons/Button";

const Collapse = ({
  title,
  variant = "primary",
  isDroppable = false,
  dragHandleProps,
  className,
  initialStateOpen = false,
  isOpen: controlledIsOpen,
  onToggle,
  children,
  hideCaret = false,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(initialStateOpen);
  const contentRef = useRef(null);
  const wasOpenRef = useRef(initialStateOpen);

  // Use controlled state if provided, otherwise use internal state
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;

  // Handle transition end: set maxHeight to "none" when fully open
  // so content can grow freely without being clipped
  const handleTransitionEnd = useCallback(() => {
    if (contentRef.current && isOpen) {
      contentRef.current.style.maxHeight = "none";
    }
  }, [isOpen]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    if (isOpen) {
      if (!wasOpenRef.current) {
        // Opening: animate from 0 to scrollHeight, then "none" on transition end
        el.style.maxHeight = `${el.scrollHeight}px`;
      } else {
        // Already open on mount: skip animation, just show content
        el.style.maxHeight = "none";
      }
    } else {
      if (wasOpenRef.current) {
        // Closing: first set explicit height so transition can animate from it
        el.style.maxHeight = `${el.scrollHeight}px`;
        // Force reflow before setting to 0
        el.offsetHeight; // eslint-disable-line no-unused-expressions
        el.style.maxHeight = "0px";
      } else {
        // Already closed on mount
        el.style.maxHeight = "0px";
      }
    }

    wasOpenRef.current = isOpen;
  }, [isOpen]);

  return (
    <div className={`collapse ${className || ""}`}>
      <div
        className={`collapse-header cursor-pointer bg-${variant} ${variant === "dark" ? "text-white" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div
          className={`p1 flex gap-1 align-center w-100 ${variant === "dark" ? "text-white" : ""}`}
        >
          {isDroppable && (
            <span
              {...(dragHandleProps || {})}
              onClick={(e) => e.stopPropagation()}
              className="collapse-drag-handle"
            >
              <FontAwesomeIcon
                className={variant === "dark" ? "text-white" : "text-dark"}
                transform={{ y: 1 }}
                icon={faGripDotsVertical}
              />
            </span>
          )}
          <span className="flex-grow">{title}</span>
        </div>
        {!hideCaret && (
          <Button
            link
            onClick={() => setIsOpen(!isOpen)}
            className="collapse-caret"
          >
            <FontAwesomeIcon
              className={variant === "dark" ? "text-white" : "text-dark"}
              size="xl"
              transform={{ y: 2 }}
              icon={isOpen ? faAngleUp : faAngleDown}
            />
          </Button>
        )}
      </div>
      <div
        ref={contentRef}
        className="collapse-body"
        onTransitionEnd={handleTransitionEnd}
      >
        {children}
      </div>
    </div>
  );
};

export default Collapse;
